import { useSeo } from "@/lib/useSeo";
import { useApi } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { HeroMockup } from "@/components/site/HeroMockup";
import { Badge, Btn, Reveal, SectionHeading, Spinner } from "@/components/site/kit";
import { CtaBanner, ProcessTimeline, ServicesGrid, StatsBar, TechStrip, WhyUs } from "@/components/site/Sections";
import { ProductGrid } from "@/components/site/ProductCard";
import { TestimonialSlider } from "@/components/site/TestimonialSlider";
import { FaqList } from "@/components/site/FaqList";
import { LeadForm } from "@/components/site/LeadForm";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { waLink } from "@/lib/api";

const Hero = ({ h }) => (
  <section className="relative overflow-hidden bg-white" data-testid="hero-section">
    <div className="dot-grid absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_top_right,#000_20%,transparent_70%)]" aria-hidden="true" />
    <div className="container-x relative grid items-center gap-16 pb-28 pt-14 sm:pt-20 lg:grid-cols-[1.05fr_1fr] lg:pb-32">
      <Reveal>
        <Badge testId="hero-badge">{h.badge}</Badge>
        <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {h.headline} <span className="text-ocean">{h.highlight}</span>
        </h1>
        <p className="mt-5 text-base font-semibold text-ocean md:text-lg">{h.subheadline}</p>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-body">{h.paragraph}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Btn to="/contact#quote" testId="hero-primary-cta">{h.primary_cta}</Btn>
          <Btn to="/products" variant="secondary" testId="hero-secondary-cta">{h.secondary_cta}</Btn>
        </div>
        <p className="mt-6 text-sm font-medium text-muted2" data-testid="hero-trust-line">{h.trust_line}</p>
      </Reveal>
      <HeroMockup />
    </div>
  </section>
);

const LeadSection = () => {
  const { settings: s } = useSite();
  return (
    <div id="quote" className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
      <Reveal>
        <Badge>Free consultation</Badge>
        <h2 className="mt-5 text-3xl font-extrabold sm:text-4xl">Tell us about your <span className="text-ocean">project</span></h2>
        <p className="mt-4 text-body">Share a few details and our experts will call you back with ideas, a timeline and a transparent quote, usually the same day.</p>
        <div className="mt-8 space-y-3">
          <a href={`tel:${s?.phone?.replace(/\s/g, "")}`} className="card-base flex items-center gap-4 p-4 card-hover" data-testid="home-call-link"><Phone className="h-5 w-5 text-ocean" /><span className="font-semibold text-navy">{s?.phone}</span></a>
          <a href={waLink(s?.whatsapp)} target="_blank" rel="noopener noreferrer" className="card-base flex items-center gap-4 p-4 card-hover" data-testid="home-whatsapp-link"><MessageCircle className="h-5 w-5 text-success" /><span className="font-semibold text-navy">Chat on WhatsApp</span></a>
          <a href={`mailto:${s?.email}`} className="card-base flex items-center gap-4 p-4 card-hover" data-testid="home-email-link"><Mail className="h-5 w-5 text-sky" /><span className="font-semibold text-navy">{s?.email}</span></a>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="card-base p-6 sm:p-8"><LeadForm source="homepage" /></div>
      </Reveal>
    </div>
  );
};

export default function Home() {
  useSeo({});
  const { data: c, isLoading } = useApi("/content/homepage");
  if (isLoading || !c) return <Spinner className="min-h-[70vh]" />;
  return (
    <>
      <Hero h={c.hero} />
      <section className="relative z-10 -mt-14 pb-6"><div className="container-x"><StatsBar stats={c.stats} /></div></section>
      <section className="section bg-page">
        <div className="container-x">
          <SectionHeading badge="What we do" title="Services that" highlight="move your business forward" subtitle="End-to-end design, development and support for software that solves real business problems." />
          <ServicesGrid />
        </div>
      </section>
      <section className="section bg-white">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading badge="Ready-made products" title="Launch faster with our" highlight="ready-made software" subtitle="Proven products you can brand as your own and start using in days." />
            <Btn to="/products" variant="secondary" className="mb-12 sm:mb-16" testId="home-all-products-btn">All Products</Btn>
          </div>
          <ProductGrid />
        </div>
      </section>
      <WhyUs why={c.why_us} />
      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading badge="Our process" title="From idea to launch in" highlight="5 clear steps" center />
          <ProcessTimeline steps={c.process} />
        </div>
      </section>
      <section className="border-y border-line bg-page py-14">
        <div className="container-x">
          <p className="mb-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted2">Technologies we work with</p>
          <TechStrip items={c.technologies} />
        </div>
      </section>
      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading badge="Testimonials" title="Loved by" highlight="growing businesses" center />
          <TestimonialSlider />
        </div>
      </section>
      <section className="section bg-page">
        <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading badge="FAQ" title="Questions?" highlight="We have answers" subtitle="Can't find what you're looking for? Message us on WhatsApp." />
          <FaqList category="home" />
        </div>
      </section>
      <section className="section bg-white">
        <div className="container-x space-y-20">
          <CtaBanner {...c.cta_banner} />
          <LeadSection />
        </div>
      </section>
    </>
  );
}
